import React, { useState, useEffect, useMemo } from 'react';
import './RegistrationList.css';

export default function RegistrationList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [filterAge, setFilterAge] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [sortBy, setSortBy] = useState("latest");

  const [page, setPage] = useState(1);
  const itemsPerPage = 15;

  const [selectedUser, setSelectedUser] = useState(null);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch("https://tdtlworld.com/workshop_registration-backend/api/registrations/");
      if (!res.ok) throw new Error("Failed to load registrations. Please try again.");
      const json = await res.json();
      setData(Array.isArray(json) ? json : (json.results || []));
    } catch (err) {
      setError(err.message || "Failed to load registrations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert(`Copied: ${text}`);
  };

  const uniqueLocations = useMemo(() => [...new Set(data.map(d => d.location).filter(Boolean))], [data]);
  const uniqueSources = useMemo(() => [...new Set(data.map(d => d.source).filter(Boolean))], [data]);
  const uniqueAges = useMemo(() => [...new Set(data.map(d => d.age).filter(Boolean))], [data]);

  const processedData = useMemo(() => {
    let filtered = data.filter(user => {
      const name = user.name || user.FullName || "";
      const matchesSearch = 
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.phone && user.phone.includes(searchTerm));
      
      const matchesAge = filterAge ? user.age === filterAge : true;
      const matchesLocation = filterLocation ? user.location === filterLocation : true;
      const matchesSource = filterSource ? user.source === filterSource : true;

      return matchesSearch && matchesAge && matchesLocation && matchesSource;
    });

    if (sortBy === "latest") {
      filtered.sort((a, b) => new Date(b.created_at || b.createdAt || b.date || 0) - new Date(a.created_at || a.createdAt || a.date || 0));
    } else if (sortBy === "name") {
      filtered.sort((a, b) => (a.name || a.FullName || "").localeCompare(b.name || b.FullName || ""));
    }

    return filtered;
  }, [data, searchTerm, filterAge, filterLocation, filterSource, sortBy]);

  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const currentData = processedData.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const getInstitution = (user) => user.schoolName || user.collegeName || user.companyName || user.institutionName || "N/A";
  const getName = (user) => user.name || user.FullName || "N/A";
  const getClass = (user) => user.class || user.std || "N/A";

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Registration Dashboard</h1>
        </div>

        <div className="controls-container">
          <input 
            type="text" 
            className="search-input" 
            placeholder="Search by name, email, or phone..." 
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          />
          
          <select className="filter-select" value={filterAge} onChange={(e) => { setFilterAge(e.target.value); setPage(1); }}>
            <option value="">All Ages</option>
            {uniqueAges.map(age => <option key={age} value={age}>{age}</option>)}
          </select>

          <select className="filter-select" value={filterLocation} onChange={(e) => { setFilterLocation(e.target.value); setPage(1); }}>
            <option value="">All Locations</option>
            {uniqueLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
          </select>

          <select className="filter-select" value={filterSource} onChange={(e) => { setFilterSource(e.target.value); setPage(1); }}>
            <option value="">All Sources</option>
            {uniqueSources.map(src => <option key={src} value={src}>{src}</option>)}
          </select>

          <select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="latest">Sort: Latest First</option>
            <option value="name">Sort: Name (A-Z)</option>
          </select>
        </div>

        {loading ? (
          <div className="loading-spinner"></div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button className="retry-btn" onClick={fetchRegistrations}>Retry</button>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Class/Prof.</th>
                    <th>Institution</th>
                    <th>Source</th>
                    <th>Lang.</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? currentData.map((user, idx) => (
                    <tr key={user.id || idx}>
                      <td>{user.id || `#${idx + 1 + (page-1)*itemsPerPage}`}</td>
                      <td style={{ fontWeight: 500, color: '#fff' }}>{getName(user)}</td>
                      <td>{user.age || "N/A"}</td>
                      <td>{user.email || "N/A"}</td>
                      <td>{user.phone || "N/A"}</td>
                      <td>{user.location || "N/A"}</td>
                      <td>{getClass(user)}</td>
                      <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={getInstitution(user)}>
                        {getInstitution(user)}
                      </td>
                      <td>{user.source || "N/A"}</td>
                      <td>{user.preferredLanguage || "N/A"}</td>
                      <td>{formatDate(user.created_at || user.createdAt || user.date)}</td>
                      <td>
                        <button className="action-btn" title="View Details" onClick={() => setSelectedUser(user)}>👁️</button>
                        <button className="action-btn" title="Copy Email" onClick={() => copyToClipboard(user.email)}>📧</button>
                        <button className="action-btn" title="Copy Phone" onClick={() => copyToClipboard(user.phone)}>📱</button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="12" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                        No registrations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="page-btn" 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span style={{ color: '#94a3b8' }}>Page <strong style={{color:'#fff'}}>{page}</strong> of <strong style={{color:'#fff'}}>{totalPages}</strong></span>
                <button 
                  className="page-btn" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal for User Details */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setSelectedUser(null)}>✕</button>
            <h2 style={{ marginBottom: '5px', marginTop: 0, color: '#00E5FF' }}>User Details</h2>
            <p style={{ color: '#94a3b8', marginTop: 0, marginBottom: '20px' }}>ID: {selectedUser.id || 'N/A'}</p>
            
            <div className="user-detail-grid">
              <div className="detail-item">
                <div className="detail-label">Full Name</div>
                <div className="detail-value">{getName(selectedUser)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Email</div>
                <div className="detail-value">{selectedUser.email || "N/A"}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Phone</div>
                <div className="detail-value">{selectedUser.phone || "N/A"}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Age Group</div>
                <div className="detail-value">{selectedUser.age || "N/A"}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Class/Profession</div>
                <div className="detail-value">{getClass(selectedUser)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Institution</div>
                <div className="detail-value">{getInstitution(selectedUser)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Location</div>
                <div className="detail-value">{selectedUser.location || "N/A"}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Source</div>
                <div className="detail-value">{selectedUser.source || "N/A"}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Language</div>
                <div className="detail-value">{selectedUser.preferredLanguage || "N/A"}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Registered At</div>
                <div className="detail-value">{formatDate(selectedUser.created_at || selectedUser.createdAt || selectedUser.date)}</div>
              </div>
              {selectedUser.parentName && (
                <div className="detail-item">
                  <div className="detail-label">Parent Name</div>
                  <div className="detail-value">{selectedUser.parentName}</div>
                </div>
              )}
              {selectedUser.parentPhone && (
                <div className="detail-item">
                  <div className="detail-label">Parent Phone</div>
                  <div className="detail-value">{selectedUser.parentPhone}</div>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '30px', justifyContent: 'flex-end' }}>
              <button className="page-btn" style={{ background: 'rgba(0, 229, 255, 0.1)' }} onClick={() => copyToClipboard(selectedUser.email)}>Copy Email</button>
              <button className="page-btn" style={{ background: 'rgba(0, 229, 255, 0.1)' }} onClick={() => copyToClipboard(selectedUser.phone)}>Copy Phone</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
